import debounce from "lodash.debounce";
import { useEffect, useMemo, useState } from "react";
import { useListContext } from "react-admin";

/**
 * Manages a debounced search input that syncs with react-admin's list filters.
 * Returns [searchQuery, setSearchQuery] where setSearchQuery both updates
 * the local input state and triggers a debounced setFilters call.
 */
export const useDebouncedListSearch = (delay = 300) => {
  const { filterValues, setFilters } = useListContext();

  const [searchQuery, setSearchQueryState] = useState<string>(
    (filterValues as { search?: string }).search || ""
  );

  const applySearch = useMemo(
    () =>
      debounce(
        (query: string, currentFilters: Record<string, unknown>) => {
          setFilters({
            ...currentFilters,
            search: query === "" ? undefined : query,
          });
        },
        delay
      ),
    [setFilters, delay]
  );

  // Cancel any pending debounce when applySearch is recreated (e.g. on page change),
  // otherwise the orphaned timer could fire the old setFilters and reset pagination.
  useEffect(() => {
    return () => applySearch.cancel();
  }, [applySearch]);

  const setSearchQuery = (value: string) => {
    setSearchQueryState(value);
    applySearch(value, filterValues as Record<string, unknown>);
  };

  // Reset local searchQuery when filters are cleared externally
  useEffect(() => {
    if (
      !Object.keys(filterValues as Record<string, unknown>).includes("search")
    ) {
      setSearchQueryState("");
    }
  }, [filterValues]);

  return [searchQuery, setSearchQuery] as const;
};
